// Empresas (autenticacion.empresas). Sistemas → Gestión de Autenticación → Empresas.
import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import QRCode from 'qrcode';
import { QRCodeSVG } from 'qrcode.react';
import { Copy, Download, Image as ImageIcon, Link as LinkIcon, Loader2, Mail, Pencil, Plus, ShieldCheck, Trash2, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import i18n from '@/lib/i18n';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { apiClient, getErrorMessage } from '@/lib/api/client';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { uuid } from '@/lib/uuid';
import { Campo, Modal, Switch, auth, fieldCls, fin } from './shared';

const FIN = { headers: { 'Content-Profile': 'financiero', 'Accept-Profile': 'financiero' } };

interface Empresa { id: string; ruc: string | null; nombre: string; razon_social?: string | null; nombre_comercial?: string | null; representante_legal?: string | null; representante_legal_id?: string | null; direccion: string | null; telefono: string | null; email: string | null; logo: string | null; login_fondo?: string | null; activo: boolean; configuracion: unknown; password_sri?: string | null; devolucion_dias_max?: number | null; devolucion_parcial_tarjeta?: string | null; recepcion_solo_principal?: boolean; recepcion_margen_factura?: number | null; ats_tolerancia?: number | null; centro_costo_modo?: string | null; es_centro_medico?: boolean; login_slug?: string | null; login_host?: string | null; pais?: string | null; moneda_funcional?: string | null; zona_horaria?: string | null }

// SMTP de autenticación (recuperación de contraseña) — autenticacion.auth_smtp_get/set.
// Opera sobre la empresa del JWT en sesión (no recibe empresa_id): aplica a la empresa activa.
interface SmtpConfig { host: string | null; puerto: number | null; seguro: boolean; usuario: string | null; from_email: string | null; from_nombre: string | null; activo: boolean; password_configurada: boolean }
const smtpVacio = { host: '', puerto: '587', seguro: false, usuario: '', password: '', from_email: '', from_nombre: '' };

// Redimensiona y comprime una imagen de fondo (login) EN EL CLIENTE antes de guardarla:
// el fondo viaja en la respuesta pública (anónima) del login, así que debe pesar poco.
// Máx. 1600px de ancho (sin agrandar) + JPEG con calidad decreciente hasta quedar bajo ~250 KB.
async function comprimirFondo(file: File): Promise<{ dataUrl: string; bytes: number }> {
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = () => reject(new Error(i18n.t('admin:emp_err_leer_img')));
      i.src = url;
    });
    const scale = Math.min(1, 1600 / img.width);
    const w = Math.max(1, Math.round(img.width * scale));
    const h = Math.max(1, Math.round(img.height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error(i18n.t('admin:emp_canvas'));
    ctx.drawImage(img, 0, 0, w, h);
    const LIMITE = 250 * 1024;
    let dataUrl = canvas.toDataURL('image/jpeg', 0.8);
    for (const q of [0.7, 0.6, 0.5, 0.4]) {
      if (dataUrl.length * 0.75 <= LIMITE) break; // base64 ≈ 4/3 del tamaño binario
      dataUrl = canvas.toDataURL('image/jpeg', q);
    }
    return { dataUrl, bytes: Math.round(dataUrl.length * 0.75) };
  } finally {
    URL.revokeObjectURL(url);
  }
}
function pesoLegible(bytes: number): string {
  return bytes >= 1024 * 1024 ? `${(bytes / (1024 * 1024)).toFixed(1)} MB` : `${Math.round(bytes / 1024)} KB`;
}

// URL pública de acceso al login con marca de la empresa (por slug). Se calcula
// SIEMPRE desde window.location.origin: cada servidor (local/remoto) debe mostrar
// su propia URL — nunca se guarda en la BD ni se hardcodea.
function urlAcceso(slug: string): string {
  const base = import.meta.env.BASE_URL; // '/erp/' local, '/' en remotos (siempre termina en '/')
  return `${window.location.origin}${base}login/${slug}`;
}
function slugArchivo(s: string): string {
  return s.trim().toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') || 'empresa';
}
const vacio = { ruc: '', razon_social: '', nombre_comercial: '', representante_legal: '', representante_legal_id: '', nombre: '', direccion: '', telefono: '', email: '', logo: '', login_fondo: '', activo: true, configuracion: '', password_sri: '', quitarClave: false, devolucion_dias_max: '0', devolucion_parcial_tarjeta: 'efectivo', recepcion_solo_principal: false, recepcion_margen_factura: '0.05', ats_tolerancia: '25.00', centro_costo_modo: 'partner', es_centro_medico: false, pais: 'EC', zona_horaria: '' };

export function Empresas() {
  const { t } = useTranslation('admin');
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const { data: rows = [], isFetching } = useQuery<Empresa[]>({ queryKey: ['adm-empresas'], queryFn: () => auth.list<Empresa>('empresas', '?select=*&order=nombre') });
  const { data: paises = [] } = useQuery<{ codigo: string; nombre: string; moneda: string | null }[]>({
    queryKey: ['adm-paises-lite'],
    queryFn: () => fin.list('paises', '?select=codigo,nombre,moneda&activo=is.true&order=nombre'),
    staleTime: 60000,
  });
  // La columna zona_horaria existe solo en instalaciones multizona (TrackConstruccion).
  const conZona = rows.some((r) => 'zona_horaria' in r);
  const { data: zonas = [] } = useQuery<{ zona: string; utc_offset: string }[]>({
    queryKey: ['adm-zonas-us'],
    queryFn: async () => (await apiClient.post('/rpc/zonas_horarias_us', {})).data,
    enabled: conZona,
    staleTime: 3600000,
  });
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<typeof vacio>(vacio);
  const [abierto, setAbierto] = useState(false);
  const [tieneClave, setTieneClave] = useState(false);
  const [probando, setProbando] = useState(false);
  const [enlaceDe, setEnlaceDe] = useState<Empresa | null>(null);
  const [copiado, setCopiado] = useState(false);
  const [hostEdit, setHostEdit] = useState('');
  const [subiendoFondo, setSubiendoFondo] = useState(false);
  const [smtpDe, setSmtpDe] = useState<Empresa | null>(null);
  const [smtpForm, setSmtpForm] = useState(smtpVacio);
  const [smtpConfigurada, setSmtpConfigurada] = useState(false);
  const [smtpPruebaDestino, setSmtpPruebaDestino] = useState('');
  const [smtpPrueba, setSmtpPrueba] = useState<{ estado: 'idle' | 'enviando' | 'enviado' | 'fallido' | 'timeout'; error: string | null }>({ estado: 'idle', error: null });
  const smtpPruebaTimers = useRef<number[]>([]);
  const limpiarTimersPrueba = () => { smtpPruebaTimers.current.forEach((id) => window.clearTimeout(id)); smtpPruebaTimers.current = []; };
  useEffect(() => () => limpiarTimersPrueba(), []);

  const { data: smtp, isFetching: cargandoSmtp } = useQuery<SmtpConfig>({
    queryKey: ['adm-empresa-smtp', smtpDe?.id],
    queryFn: async () => (await apiClient.post('/rpc/auth_smtp_get', {})).data as SmtpConfig,
    enabled: !!smtpDe,
  });

  const abrirSmtp = (e: Empresa) => {
    setSmtpForm(smtpVacio); setSmtpConfigurada(false); setSmtpDe(e);
    limpiarTimersPrueba();
    setSmtpPruebaDestino(''); setSmtpPrueba({ estado: 'idle', error: null });
  };
  const cerrarSmtp = () => { limpiarTimersPrueba(); setSmtpDe(null); };

  // Al cargar la config existente, precarga los campos (menos la clave: write-only).
  useEffect(() => {
    if (!smtp) return;
    setSmtpForm({
      host: smtp.host ?? '', puerto: String(smtp.puerto ?? 587), seguro: !!smtp.seguro,
      usuario: smtp.usuario ?? '', password: '', from_email: smtp.from_email ?? '', from_nombre: smtp.from_nombre ?? '',
    });
    setSmtpConfigurada(!!smtp.password_configurada);
    // Conveniencia: sugiere el from_email como destino de prueba si el usuario no ha escrito nada.
    setSmtpPruebaDestino((d) => d || smtp.from_email || '');
  }, [smtp]);

  // Encola un correo de prueba con la config SMTP GUARDADA y sondea su estado hasta ~20s.
  const enviarPrueba = async () => {
    if (!smtpPruebaDestino.includes('@')) return;
    limpiarTimersPrueba();
    setSmtpPrueba({ estado: 'enviando', error: null });
    try {
      const { data } = await apiClient.post('/rpc/auth_smtp_probar', { _destino: smtpPruebaDestino });
      const envioId = data?.envio_id as string | undefined;
      if (!envioId) throw new Error(t('emp_err_sin_id_envio'));
      const MAX_INTENTOS = 12;
      let intentos = 0;
      const sondear = () => {
        intentos += 1;
        apiClient.post('/rpc/auth_smtp_prueba_estado', { _id: envioId })
          .then((r) => {
            const estado = r.data?.estado as string | undefined;
            if (estado === 'enviado') { setSmtpPrueba({ estado: 'enviado', error: null }); return; }
            if (estado === 'fallido') { setSmtpPrueba({ estado: 'fallido', error: (r.data?.error as string | null) ?? null }); return; }
            if (intentos >= MAX_INTENTOS) { setSmtpPrueba({ estado: 'timeout', error: null }); return; }
            const id = window.setTimeout(sondear, 1500);
            smtpPruebaTimers.current.push(id);
          })
          .catch((e) => setSmtpPrueba({ estado: 'fallido', error: getErrorMessage(e) }));
      };
      const id = window.setTimeout(sondear, 1500);
      smtpPruebaTimers.current.push(id);
    } catch (e) {
      setSmtpPrueba({ estado: 'idle', error: null });
      toast.error(getErrorMessage(e));
    }
  };

  const guardarSmtp = useMutation({
    mutationFn: async () => {
      await apiClient.post('/rpc/auth_smtp_set', {
        _host: smtpForm.host || null,
        _port: Math.max(1, Number(smtpForm.puerto) || 587),
        _secure: smtpForm.seguro,
        _username: smtpForm.usuario || null,
        _password: smtpForm.password || null,
        _from_email: smtpForm.from_email || null,
        _from_nombre: smtpForm.from_nombre || null,
      });
    },
    onSuccess: () => {
      toast.success(t('emp_smtp_guardada'));
      setSmtpForm((f) => ({ ...f, password: '' }));
      void qc.invalidateQueries({ queryKey: ['adm-empresa-smtp'] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const copiarUrl = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiado(true);
      toast.success(t('emp_enlace_copiado'));
      setTimeout(() => setCopiado(false), 2000);
    } catch { toast.error(t('emp_err_copiar')); }
  };

  const descargarQr = async (url: string, empresaNombre: string) => {
    try {
      const dataUrl = await QRCode.toDataURL(url, { width: 512, margin: 2 });
      const a = document.createElement('a');
      a.href = dataUrl;
      a.download = `acceso-${slugArchivo(empresaNombre)}.png`;
      a.click();
    } catch { toast.error(t('emp_err_qr')); }
  };

  // Prueba el acceso al portal SRI (guarda la clave si escribiste una nueva, encola
  // un job 'test' que hace login, y sondea el resultado). Solo la empresa actual.
  const probarSri = async () => {
    if (!editId) return;
    setProbando(true);
    try {
      if (form.password_sri.trim()) {
        await apiClient.post('/rpc/set_empresa_password_sri', { _empresa_id: editId, _password_sri: form.password_sri });
        setTieneClave(true);
      }
      const { job_id } = (await apiClient.post('/rpc/sri_probar_credenciales', { _empresa_id: editId }, FIN)).data as { job_id: string };
      const t0 = Date.now();
      while (Date.now() - t0 < 100000) {
        await new Promise((r) => setTimeout(r, 2500));
        const est = (await apiClient.post('/rpc/sri_job_estado', { _job_id: job_id, _desde_evento: 0 }, FIN)).data as { job?: { estado?: string; mensaje_error?: string | null } };
        const estado = est?.job?.estado;
        if (estado === 'completado') { toast.success(t('emp_sri_ok')); return; }
        if (estado === 'error') { toast.error(est?.job?.mensaje_error || t('emp_sri_err')); return; }
      }
      toast.message(t('emp_sri_curso'));
    } catch (e) { toast.error(getErrorMessage(e)); }
    finally { setProbando(false); }
  };

  const abrir = (e?: Empresa) => {
    if (e) setForm({ ruc: e.ruc ?? '', razon_social: e.razon_social ?? '', nombre_comercial: e.nombre_comercial ?? '', representante_legal: e.representante_legal ?? '', representante_legal_id: e.representante_legal_id ?? '', nombre: e.nombre, direccion: e.direccion ?? '', telefono: e.telefono ?? '', email: e.email ?? '', logo: e.logo ?? '', login_fondo: e.login_fondo ?? '', activo: e.activo, configuracion: e.configuracion ? JSON.stringify(e.configuracion, null, 2) : '', password_sri: '', quitarClave: false, devolucion_dias_max: String(e.devolucion_dias_max ?? 0), devolucion_parcial_tarjeta: e.devolucion_parcial_tarjeta ?? 'efectivo', recepcion_solo_principal: !!e.recepcion_solo_principal, recepcion_margen_factura: String(e.recepcion_margen_factura ?? 0.05), ats_tolerancia: String(e.ats_tolerancia ?? 25), centro_costo_modo: e.centro_costo_modo ?? 'partner', es_centro_medico: !!e.es_centro_medico, pais: e.pais ?? 'EC', zona_horaria: e.zona_horaria ?? '' });
    else setForm(vacio);
    setTieneClave(!!(e && e.password_sri)); // clave del portal SRI ya guardada (cifrada); nunca se muestra su valor
    setEditId(e?.id ?? null); setAbierto(true);
  };
  // Sube el logo leyéndolo en el navegador y guardándolo incrustado (data URL).
  // No hay backend de archivos; la columna `logo` (varchar) almacena el base64.
  const leerLogo = (file: File) => {
    if (!file.type.startsWith('image/')) { toast.error(t('emp_err_img')); return; }
    if (file.size > 512 * 1024) { toast.error(t('emp_err_img_grande')); return; }
    const reader = new FileReader();
    reader.onload = () => setForm((f) => ({ ...f, logo: String(reader.result) }));
    reader.onerror = () => toast.error(t('emp_err_leer_arch'));
    reader.readAsDataURL(file);
  };

  // Sube el fondo del login: se redimensiona/comprime en el navegador (ver
  // comprimirFondo) antes de guardarlo, porque viaja en la respuesta pública
  // (anónima) del login y un fondo sin comprimir pesa mucho más que un logo.
  const leerFondo = async (file: File) => {
    if (!file.type.startsWith('image/')) { toast.error(t('emp_err_img')); return; }
    setSubiendoFondo(true);
    try {
      const { dataUrl } = await comprimirFondo(file);
      setForm((f) => ({ ...f, login_fondo: dataUrl }));
    } catch (e) { toast.error(getErrorMessage(e)); }
    finally { setSubiendoFondo(false); }
  };

  const guardar = useMutation({
    mutationFn: async () => {
      let configuracion: unknown = null;
      if (form.configuracion.trim()) {
        try { configuracion = JSON.parse(form.configuracion); } catch { throw new Error(t('emp_err_json')); }
      }
      const body: Record<string, unknown> = {
        ruc: form.ruc || null,
        razon_social: form.razon_social || null, nombre_comercial: form.nombre_comercial || null,
        representante_legal: form.representante_legal || null, representante_legal_id: form.representante_legal_id || null,
        // `nombre` = nombre para mostrar (comercial si existe, si no la razón social).
        nombre: form.nombre_comercial.trim() || form.razon_social.trim(),
        direccion: form.direccion || null, telefono: form.telefono || null,
        email: form.email || null, logo: form.logo || null, login_fondo: form.login_fondo || null, activo: form.activo, configuracion,
        devolucion_dias_max: Math.max(0, Number(form.devolucion_dias_max) || 0),
        devolucion_parcial_tarjeta: form.devolucion_parcial_tarjeta,
        recepcion_solo_principal: form.recepcion_solo_principal,
        recepcion_margen_factura: Math.max(0, Number(form.recepcion_margen_factura) || 0),
        ats_tolerancia: Math.max(0, Number(form.ats_tolerancia) || 0),
        centro_costo_modo: form.centro_costo_modo,
        es_centro_medico: form.es_centro_medico,
        pais: form.pais || 'EC',   // la moneda funcional la deriva el trigger desde el catálogo de países
        updated_at: new Date().toISOString(),
      };
      // Zona horaria: solo en instalaciones cuya BD la tiene (TrackConstruccion, script 005).
      if (conZona && form.zona_horaria) body.zona_horaria = form.zona_horaria;
      const id = editId ?? uuid();
      if (editId) await auth.update('empresas', editId, body);
      else await auth.create('empresas', { ...body, id });
      // Clave del portal SRI: de SOLO escritura, vía RPC admin (se guarda cifrada).
      // En blanco = no se toca; casilla "Quitar" = se borra (envía cadena vacía).
      if (form.quitarClave || form.password_sri.trim()) {
        await apiClient.post('/rpc/set_empresa_password_sri', { _empresa_id: id, _password_sri: form.quitarClave ? '' : form.password_sri });
      }
    },
    onSuccess: () => { toast.success(t('emp_guardada')); setAbierto(false); void qc.invalidateQueries({ queryKey: ['adm-empresas'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const eliminar = useMutation({
    mutationFn: (id: string) => auth.remove('empresas', id),
    onSuccess: () => { toast.success(t('emp_eliminada')); void qc.invalidateQueries({ queryKey: ['adm-empresas'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  // Dominio propio (script 904): quien entre por este host ve el login de esta
  // empresa sin slug en la URL. Se normaliza en el cliente (minúsculas, sin
  // esquema ni ruta ni www) para que el índice único de la BD no rechace por
  // una diferencia meramente cosmética.
  const normalizarHost = (v: string) =>
    v.trim().toLowerCase()
      .replace(/^https?:\/\//, '')
      .replace(/^www\./, '')
      .replace(/[/:].*$/, '');

  const guardarHost = useMutation({
    mutationFn: async () => {
      if (!enlaceDe) return;
      const limpio = normalizarHost(hostEdit);
      await auth.update('empresas', enlaceDe.id, { login_host: limpio || null });
      return limpio;
    },
    onSuccess: (limpio) => {
      toast.success(limpio ? t('emp_dom_asignado', { d: limpio }) : t('emp_dom_quitado'));
      setEnlaceDe((prev) => (prev ? { ...prev, login_host: (limpio as string) || null } : prev));
      setHostEdit((limpio as string) || '');
      void qc.invalidateQueries({ queryKey: ['adm-empresas'] });
    },
    onError: (e) => {
      const msg = getErrorMessage(e);
      // 23505 = índice único: ese dominio ya es de otra empresa.
      toast.error(/duplicate|unique|23505/i.test(msg)
        ? t('emp_dom_dup')
        : msg);
    },
  });

  return (
    <div className="p-6 space-y-4">
      {dlg.ui}
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">{t('emp_titulo')}</h1>
        <Button onClick={() => abrir()}><Plus className="w-4 h-4 mr-1" /> {t('emp_nueva')}</Button>
      </div>
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">{t('emp_card', { n: rows.length })}</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">{t('emp_c_nombre')}</th><th className="py-2 pr-3">{t('emp_c_ruc')}</th><th className="py-2 pr-3">{t('comun_email')}</th><th className="py-2 pr-3">{t('emp_c_tel')}</th><th className="py-2 pr-3">{t('comun_estado')}</th><th className="py-2"></th>
            </tr></thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={6} className="py-8 text-center text-muted-foreground">{t('emp_vacio')}</td></tr>}
              {rows.map((e) => (
                <tr key={e.id} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-2 pr-3 font-medium">{e.nombre}</td>
                  <td className="py-2 pr-3 font-mono">{e.ruc || '—'}</td>
                  <td className="py-2 pr-3 text-muted-foreground">{e.email || '—'}</td>
                  <td className="py-2 pr-3 font-mono">{e.telefono || '—'}</td>
                  <td className="py-2 pr-3">{e.activo ? t('emp_activa') : t('emp_inactiva')}</td>
                  <td className="py-2 text-right whitespace-nowrap">
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title={t('emp_tip_enlace')} onClick={() => { setEnlaceDe(e); setHostEdit(e.login_host || ''); }}><LinkIcon className="w-4 h-4" /></button>
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted ml-1" title={t('emp_tip_smtp')} onClick={() => abrirSmtp(e)}><Mail className="w-4 h-4" /></button>
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted ml-1" title={t('comun_editar')} onClick={() => abrir(e)}><Pencil className="w-4 h-4" /></button>
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md text-destructive hover:bg-destructive/10 ml-1" title={t('comun_eliminar')} onClick={async () => { if (await dlg.confirmar(t('emp_confirmar_eliminar', { nombre: e.nombre }), { peligro: true })) eliminar.mutate(e.id); }}><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {abierto && (
        <Modal title={editId ? t('emp_editar') : t('emp_nueva')} onClose={() => setAbierto(false)} wide>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Campo label={t('emp_f_razon')} className="col-span-2"><input className={fieldCls} value={form.razon_social} onChange={(e) => setForm({ ...form, razon_social: e.target.value })} /></Campo>
              <Campo label={t('emp_f_comercial')} className="col-span-2"><input className={fieldCls} placeholder={t('emp_ph_comercial')} value={form.nombre_comercial} onChange={(e) => setForm({ ...form, nombre_comercial: e.target.value })} /></Campo>
              <Campo label={t('emp_f_ruc')}><input className={fieldCls} maxLength={13} value={form.ruc} onChange={(e) => setForm({ ...form, ruc: e.target.value })} /></Campo>
              <Campo label={t('emp_f_pais')}>
                <select className={fieldCls} value={form.pais} onChange={(e) => setForm({ ...form, pais: e.target.value })}>
                  {paises.map((p) => <option key={p.codigo} value={p.codigo}>{p.nombre}{p.moneda ? ` (${p.moneda})` : ''}</option>)}
                  {form.pais && !paises.some((p) => p.codigo === form.pais) && <option value={form.pais}>{form.pais}</option>}
                </select>
                <p className="mt-1 text-[11px] text-muted-foreground">{t('emp_pais_hint')}</p>
              </Campo>
              {conZona && (
                <Campo label={t('emp_f_zona')}>
                  <select className={fieldCls} value={form.zona_horaria} onChange={(e) => setForm({ ...form, zona_horaria: e.target.value })}>
                    {!form.zona_horaria && <option value="">{t('emp_zona_defecto')}</option>}
                    {zonas.map((z) => <option key={z.zona} value={z.zona}>{z.zona.replace(/_/g, ' ')} (UTC{z.utc_offset})</option>)}
                    {form.zona_horaria && !zonas.some((z) => z.zona === form.zona_horaria) && <option value={form.zona_horaria}>{form.zona_horaria}</option>}
                  </select>
                  <p className="mt-1 text-[11px] text-muted-foreground">{t('emp_zona_hint')}</p>
                </Campo>
              )}
              <Campo label={t('emp_f_email')}><input type="email" className={fieldCls} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Campo>
              <Campo label={t('emp_f_tel')} className="col-span-2"><input className={fieldCls} value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })} /></Campo>
              <Campo label={t('emp_f_rep')}><input className={fieldCls} placeholder={t('emp_ph_rep')} value={form.representante_legal} onChange={(e) => setForm({ ...form, representante_legal: e.target.value })} /></Campo>
              <Campo label={t('emp_f_rep_id')}><input className={fieldCls} maxLength={20} value={form.representante_legal_id} onChange={(e) => setForm({ ...form, representante_legal_id: e.target.value })} /></Campo>
              <p className="col-span-2 -mt-1 text-[11px] text-muted-foreground">{t('emp_rep_hint')}</p>
              <Campo label={t('emp_f_logo')} className="col-span-2">
                <div className="flex items-center gap-3">
                  {form.logo
                    ? <img src={form.logo} alt="logo" className="h-14 w-14 rounded-md border bg-background object-contain" />
                    : <div className="flex h-14 w-14 items-center justify-center rounded-md border bg-muted text-muted-foreground"><ImageIcon className="h-5 w-5" /></div>}
                  <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-sm hover:bg-muted">
                    <Upload className="h-4 w-4" /> {t('emp_subir')}
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) leerLogo(f); e.target.value = ''; }} />
                  </label>
                  {form.logo && <button type="button" className="text-xs text-destructive hover:underline" onClick={() => setForm({ ...form, logo: '' })}>{t('comun_quitar')}</button>}
                </div>
                <p className="mt-1 text-[11px] text-muted-foreground">{t('emp_logo_hint')}</p>
              </Campo>
              <Campo label={t('emp_f_fondo')} className="col-span-2">
                <div className="flex items-center gap-3">
                  {form.login_fondo
                    ? <img src={form.login_fondo} alt={t('emp_alt_fondo')} className="h-14 w-24 rounded-md border bg-background object-cover" />
                    : <div className="flex h-14 w-24 items-center justify-center rounded-md border bg-muted text-muted-foreground"><ImageIcon className="h-5 w-5" /></div>}
                  <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-sm hover:bg-muted">
                    {subiendoFondo ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Subir imagen
                    <input type="file" accept="image/*" className="hidden" disabled={subiendoFondo} onChange={(e) => { const f = e.target.files?.[0]; if (f) void leerFondo(f); e.target.value = ''; }} />
                  </label>
                  {form.login_fondo && <button type="button" className="text-xs text-destructive hover:underline" onClick={() => setForm({ ...form, login_fondo: '' })}>{t('comun_quitar')}</button>}
                </div>
                <p className="mt-1 text-[11px] text-muted-foreground">
                  {t('emp_fondo_hint')}
                  {form.login_fondo && t('emp_fondo_peso', { peso: pesoLegible(Math.round(form.login_fondo.length * 0.75)) })}
                </p>
              </Campo>
              <Campo label={t('emp_f_plazo')}><input type="number" min={0} className={fieldCls} value={form.devolucion_dias_max} onChange={(e) => setForm({ ...form, devolucion_dias_max: e.target.value })} placeholder={t('emp_ph_plazo')} /></Campo>
              {/* 898: el reverso a la tarjeta es total o nada, así que una
                  devolución PARCIAL de una venta con tarjeta va en efectivo o al
                  monedero. Esto elige cuál viene preseleccionada. */}
              <Campo label={t('emp_f_dev_parcial')}>
                <select className={fieldCls} value={form.devolucion_parcial_tarjeta}
                        onChange={(e) => setForm({ ...form, devolucion_parcial_tarjeta: e.target.value })}>
                  <option value="efectivo">{t('emp_dev_efectivo')}</option>
                  <option value="monedero">{t('emp_dev_monedero')}</option>
                </select>
                <span className="mt-1 block text-[11px] text-muted-foreground">
                  {t('emp_dev_hint')}
                </span>
              </Campo>
              <Campo label={t('emp_f_margen')}>
                <input type="number" min={0} step="0.01" className={fieldCls} value={form.recepcion_margen_factura} onChange={(e) => setForm({ ...form, recepcion_margen_factura: e.target.value })} placeholder="0.05" />
                <p className="mt-1 text-[11px] text-muted-foreground">{t('emp_margen_hint')}</p>
              </Campo>
              <Campo label={t('emp_f_ats')}>
                <input type="number" min={0} step="0.01" className={fieldCls} value={form.ats_tolerancia} onChange={(e) => setForm({ ...form, ats_tolerancia: e.target.value })} placeholder="25.00" />
                <p className="mt-1 text-[11px] text-muted-foreground">{t('emp_ats_hint')}</p>
              </Campo>
              <Campo label={t('emp_f_cc')} className="col-span-2">
                <select className={fieldCls} value={form.centro_costo_modo} onChange={(e) => setForm({ ...form, centro_costo_modo: e.target.value })}>
                  <option value="partner">{t('emp_cc_partner')}</option>
                  <option value="contable">{t('emp_cc_contable')}</option>
                </select>
                <p className="mt-1 text-[11px] text-muted-foreground">{t('emp_cc_hint')}</p>
              </Campo>
              <Campo label={t('emp_f_dir')} className="col-span-2"><textarea className={`${fieldCls} h-16`} value={form.direccion} onChange={(e) => setForm({ ...form, direccion: e.target.value })} /></Campo>
              <Campo label={t('emp_f_clave_sri')} className="col-span-2">
                <input
                  type="password"
                  autoComplete="new-password"
                  className={fieldCls}
                  disabled={form.quitarClave}
                  value={form.password_sri}
                  onChange={(e) => setForm({ ...form, password_sri: e.target.value })}
                  placeholder={tieneClave ? t('emp_ph_clave_hay') : t('emp_ph_clave_sri')}
                />
                <p className="mt-1 text-[11px] text-muted-foreground">
                  {tieneClave ? t('emp_clave_hay') : t('emp_clave_no')}{t('emp_clave_uso')}
                </p>
                {editId && (
                  <div className="mt-2 flex items-center gap-2">
                    <Button size="sm" variant="outline" isLoading={probando} disabled={probando || form.quitarClave} onClick={() => void probarSri()}>
                      {!probando && <ShieldCheck className="mr-1 h-4 w-4" />} {t('emp_probar_sri')}
                    </Button>
                    <span className="text-[11px] text-muted-foreground">{t('emp_probar_hint')}</span>
                  </div>
                )}
                {tieneClave && (
                  <label className="mt-1 flex items-center gap-2 text-xs text-destructive">
                    <input type="checkbox" checked={form.quitarClave} onChange={(e) => setForm({ ...form, quitarClave: e.target.checked, password_sri: e.target.checked ? '' : form.password_sri })} />
                    {t('emp_quitar_clave')}
                  </label>
                )}
              </Campo>
            </div>
            <Campo label={t('emp_f_config')}><textarea className={`${fieldCls} h-24 font-mono text-xs`} value={form.configuracion} onChange={(e) => setForm({ ...form, configuracion: e.target.value })} placeholder="{ }" /></Campo>
            <Switch checked={form.recepcion_solo_principal} onChange={(v) => setForm({ ...form, recepcion_solo_principal: v })} label={t('emp_sw_recep')} />
            <Switch checked={form.es_centro_medico} onChange={(v) => setForm({ ...form, es_centro_medico: v })} label={t('emp_sw_medico')} />
            <Switch checked={form.activo} onChange={(v) => setForm({ ...form, activo: v })} label={t('emp_sw_activa')} />
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setAbierto(false)}>{t('comun_cancelar')}</Button>
              <Button isLoading={guardar.isPending} disabled={!form.razon_social.trim()} onClick={() => guardar.mutate()}>{t('comun_guardar')}</Button>
            </div>
          </div>
        </Modal>
      )}

      {enlaceDe && (
        <Modal title={t('emp_enlace_titulo', { nombre: enlaceDe.nombre })} onClose={() => setEnlaceDe(null)}>
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">
              {t('emp_enlace_intro')}
            </p>
            {!enlaceDe.login_slug ? (
              <p className="rounded-md border border-dashed p-3 text-sm text-muted-foreground">{t('emp_sin_slug')}</p>
            ) : (
              <>
                <Campo label={t('emp_f_slug')}><input className={fieldCls} readOnly value={enlaceDe.login_slug} /></Campo>
                <Campo label={t('emp_f_url')}>
                  <div className="flex items-center gap-2">
                    <input className={`${fieldCls} font-mono text-xs`} readOnly value={urlAcceso(enlaceDe.login_slug)} onFocus={(e) => e.currentTarget.select()} />
                    <Button size="sm" variant="outline" onClick={() => void copiarUrl(urlAcceso(enlaceDe.login_slug as string))}>
                      <Copy className="mr-1 h-4 w-4" /> {copiado ? t('emp_copiado') : t('emp_copiar')}
                    </Button>
                  </div>
                </Campo>
                <div className="flex flex-col items-center gap-2 pt-2">
                  <div className="inline-block rounded-lg bg-white p-3">
                    <QRCodeSVG value={urlAcceso(enlaceDe.login_slug)} size={180} includeMargin />
                  </div>
                  <Button size="sm" variant="outline" onClick={() => void descargarQr(urlAcceso(enlaceDe.login_slug as string), enlaceDe.nombre)}>
                    <Download className="mr-1 h-4 w-4" /> {t('emp_desc_qr')}
                  </Button>
                </div>

                {/* Dominio propio — alternativa al slug para empresas con subdominio */}
                <div className="border-t pt-3">
                  <Campo label={t('emp_f_dominio')}>
                    <div className="flex items-center gap-2">
                      <input
                        className={`${fieldCls} font-mono text-xs`}
                        value={hostEdit}
                        onChange={(ev) => setHostEdit(ev.target.value)}
                        placeholder={t('emp_ph_dominio')}
                        disabled={guardarHost.isPending}
                      />
                      <Button size="sm" onClick={() => guardarHost.mutate()} disabled={guardarHost.isPending}>
                        {guardarHost.isPending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
                        {t('comun_guardar')}
                      </Button>
                    </div>
                  </Campo>
                  <p className="mt-1 text-[11px] text-muted-foreground">
                    {t('emp_dominio_hint')}
                  </p>
                </div>
              </>
            )}
            <div className="flex justify-end pt-2">
              <Button variant="outline" onClick={() => setEnlaceDe(null)}>{t('comun_cerrar')}</Button>
            </div>
          </div>
        </Modal>
      )}

      {smtpDe && (
        <Modal title={t('emp_smtp_titulo', { nombre: smtpDe.nombre })} onClose={cerrarSmtp}>
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">
              {t('emp_smtp_intro_a')}{t('emp_smtp_intro_b')}{t('emp_smtp_intro_c')}
            </p>
            <p className="rounded-md border border-dashed p-2 text-[11px] text-muted-foreground">
              {t('emp_smtp_sesion_a')}<strong>{t('emp_smtp_sesion_b')}</strong>{t('emp_smtp_sesion_c')}
            </p>
            {cargandoSmtp ? (
              <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <Campo label={t('emp_f_host')} className="col-span-2"><input className={fieldCls} placeholder="smtp.midominio.com" value={smtpForm.host} onChange={(ev) => setSmtpForm({ ...smtpForm, host: ev.target.value })} /></Campo>
                  <Campo label={t('emp_f_puerto')}><input type="number" min={1} className={fieldCls} value={smtpForm.puerto} onChange={(ev) => setSmtpForm({ ...smtpForm, puerto: ev.target.value })} /></Campo>
                  <Campo label={t('comun_usuario')}><input className={fieldCls} value={smtpForm.usuario} onChange={(ev) => setSmtpForm({ ...smtpForm, usuario: ev.target.value })} /></Campo>
                  <Campo label={t('emp_f_remitente')} className="col-span-2"><input type="email" className={fieldCls} placeholder="no-responder@midominio.com" value={smtpForm.from_email} onChange={(ev) => setSmtpForm({ ...smtpForm, from_email: ev.target.value })} /></Campo>
                  <Campo label={t('emp_f_nom_remitente')} className="col-span-2"><input className={fieldCls} placeholder={t('emp_ph_nom_remitente')} value={smtpForm.from_nombre} onChange={(ev) => setSmtpForm({ ...smtpForm, from_nombre: ev.target.value })} /></Campo>
                  <Campo label={t('emp_f_pass')} className="col-span-2">
                    <input
                      type="password"
                      autoComplete="new-password"
                      className={fieldCls}
                      value={smtpForm.password}
                      onChange={(ev) => setSmtpForm({ ...smtpForm, password: ev.target.value })}
                      placeholder={smtpConfigurada ? t('emp_ph_pass_sin') : t('emp_ph_pass_smtp')}
                    />
                    <p className="mt-1 text-[11px] text-muted-foreground">
                      {smtpConfigurada ? t('emp_smtp_clave_hay') : t('emp_clave_no')}{t('emp_smtp_nunca')}
                    </p>
                  </Campo>
                </div>
                <Switch checked={smtpForm.seguro} onChange={(v) => setSmtpForm({ ...smtpForm, seguro: v })} label={t('emp_sw_tls')} />

                <div className="rounded-md border border-dashed p-3 space-y-2">
                  <p className="text-xs font-medium">{t('emp_prueba_titulo')}</p>
                  <div className="flex gap-2">
                    <input
                      type="email"
                      className={`${fieldCls} flex-1`}
                      placeholder="destino@correo.com"
                      value={smtpPruebaDestino}
                      onChange={(ev) => setSmtpPruebaDestino(ev.target.value)}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      isLoading={smtpPrueba.estado === 'enviando'}
                      disabled={smtpPrueba.estado === 'enviando' || !smtpPruebaDestino.includes('@')}
                      onClick={() => void enviarPrueba()}
                    >
                      {t('emp_prueba_titulo')}
                    </Button>
                  </div>
                  <p className="text-[11px] text-muted-foreground">
                    {t('emp_prueba_hint')}
                  </p>
                  {smtpPrueba.estado === 'enviando' && (
                    <p className="flex items-center gap-1 text-xs text-muted-foreground"><Loader2 className="w-3.5 h-3.5 animate-spin" /> {t('emp_enviando')}</p>
                  )}
                  {smtpPrueba.estado === 'enviado' && (
                    <p className="text-xs text-emerald-600">{t('emp_prueba_ok', { d: smtpPruebaDestino })}</p>
                  )}
                  {smtpPrueba.estado === 'fallido' && (
                    <p className="text-xs text-destructive">✗ {smtpPrueba.error || t('emp_prueba_fallo')}</p>
                  )}
                  {smtpPrueba.estado === 'timeout' && (
                    <p className="text-xs text-amber-600">{t('emp_prueba_timeout')}</p>
                  )}
                </div>

                <div className="flex justify-end gap-2 pt-2">
                  <Button variant="outline" onClick={cerrarSmtp}>{t('comun_cerrar')}</Button>
                  <Button isLoading={guardarSmtp.isPending} disabled={!smtpForm.host.trim()} onClick={() => guardarSmtp.mutate()}>{t('comun_guardar')}</Button>
                </div>
              </>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
}
